'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Eye, EyeOff, Loader2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import DemoCredentials from './DemoCredentials';

interface LoginFormData {
  email: string;
  password: string;
}

interface LoginFormProps {
  onSuccess: () => void;
}

// Mock demo accounts — backend integration point: replace with real auth
const demoAccounts = [
  { role: 'User A', email: 'priya.sharma@demo.saath.in', password: 'DemoSaath@2026' },
  { role: 'User B', email: 'arjun.mehta@demo.saath.in', password: 'DemoSaath@2026' },
  { role: 'Admin', email: 'admin@saath.in', password: 'AdminSaath@2026' },
];

export default function LoginForm({ onSuccess }: LoginFormProps) {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [authError, setAuthError] = useState('');

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<LoginFormData>({ mode: 'onBlur' });

  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true);
    setAuthError('');

    // Backend integration point: POST /api/v1/auth/login
    await new Promise((r) => setTimeout(r, 1200));

    const matched = demoAccounts.find(
      (a) => a.email === data.email && a.password === data.password
    );

    if (matched) {
      toast.success('Welcome back! Signing you in…');
      setTimeout(onSuccess, 800);
    } else {
      setAuthError('Invalid credentials — use the demo accounts below to sign in.');
    }

    setIsLoading(false);
  };

  const handleDemoFill = (email: string, password: string) => {
    setValue('email', email, { shouldValidate: true });
    setValue('password', password, { shouldValidate: true });
    setAuthError('');
  };

  return (
    <div>
      <div className="mb-7">
        <h2 className="font-display text-2xl font-semibold text-foreground mb-1">Welcome back</h2>
        <p className="text-sm text-muted-foreground">Sign in to continue your journey</p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-5">
        {/* Auth error */}
        {authError && (
          <div className="flex items-start gap-3 p-3.5 rounded-xl bg-error/8 border border-error/20 fade-in">
            <AlertCircle size={15} className="text-error flex-shrink-0 mt-0.5" />
            <p className="text-sm text-error leading-snug">{authError}</p>
          </div>
        )}

        {/* Email */}
        <div>
          <label htmlFor="login-email" className="block text-sm font-semibold text-foreground mb-1.5">
            Email address
          </label>
          <input
            id="login-email"
            type="email"
            autoComplete="email"
            placeholder="your@email.com"
            className={`w-full h-11 px-4 rounded-xl border text-sm transition-all duration-150 outline-none bg-card placeholder:text-muted-foreground/50
              focus:border-primary focus:ring-2 focus:ring-primary/12
              ${errors.email ? 'border-error bg-error/4' : 'border-border hover:border-muted-foreground/40'}`}
            {...register('email', {
              required: 'Email is required',
              pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email address' },
            })}
          />
          {errors.email && (
            <p className="mt-1.5 text-xs text-error flex items-center gap-1">
              <AlertCircle size={11} /> {errors.email.message}
            </p>
          )}
        </div>

        {/* Password */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label htmlFor="login-password" className="block text-sm font-semibold text-foreground">
              Password
            </label>
            <button
              type="button"
              className="text-xs text-primary font-medium hover:underline transition-colors"
              onClick={() => toast.info('Password reset — feature coming soon')}
            >
              Forgot password?
            </button>
          </div>
          <div className="relative">
            <input
              id="login-password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              placeholder="Your password"
              className={`w-full h-11 px-4 pr-11 rounded-xl border text-sm transition-all duration-150 outline-none bg-card placeholder:text-muted-foreground/50
                focus:border-primary focus:ring-2 focus:ring-primary/12
                ${errors.password ? 'border-error bg-error/4' : 'border-border hover:border-muted-foreground/40'}`}
              {...register('password', {
                required: 'Password is required',
                minLength: { value: 8, message: 'Password must be at least 8 characters' },
              })}
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors p-1"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          {errors.password && (
            <p className="mt-1.5 text-xs text-error flex items-center gap-1">
              <AlertCircle size={11} /> {errors.password.message}
            </p>
          )}
        </div>

        {/* Remember me */}
        <div className="flex items-center gap-2.5">
          <input
            id="remember-me"
            type="checkbox"
            className="w-4 h-4 rounded border-border accent-primary cursor-pointer"
          />
          <label htmlFor="remember-me" className="text-sm text-muted-foreground cursor-pointer select-none">
            Keep me signed in
          </label>
        </div>

        {/* Submit */}
        <button
          type="submit"
          disabled={isLoading}
          className="w-full h-11 rounded-xl brand-gradient text-white text-sm font-semibold shadow-primary-custom btn-press disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-opacity"
        >
          {isLoading ? (
            <>
              <Loader2 size={16} className="animate-spin" />
              <span>Signing in…</span>
            </>
          ) : (
            'Sign In'
          )}
        </button>
      </form>

      {/* Demo credentials */}
      <div className="mt-6">
        <DemoCredentials accounts={demoAccounts} onSelect={handleDemoFill} />
      </div>
    </div>
  );
}